import api from "./api";
import { getSessionToken } from "./sessionToken";

// This channel carries invalidations only. Rights always come from /auth/me.
export function startGoogleBillingEvents({ session, refresh, WebSocketClass = WebSocket }) {
  let socket, timer, watchdog, stopped = false, active = true, delay = 1000;
  let refreshing = false, again = false;
  const valid = () => !stopped && getSessionToken() === session;
  async function reload() {
    if (!valid()) return;
    if (refreshing) { again = true; return; }
    refreshing = true;
    try {
      do { again = false; await refresh(); } while (again && valid());
    } catch {
      // Reconnect and reload if the authoritative read failed after delivery.
      socket?.close();
    } finally { refreshing = false; }
  }
  function disconnect() {
    clearTimeout(timer); clearTimeout(watchdog);
    const previous = socket; socket = null;
    if (previous) { previous.onclose = null; previous.close(); }
  }
  function connect() {
    if (!valid() || !active || navigator.onLine === false || socket) return;
    const url = new URL(`${api.defaults.baseURL}/iap/google/events`);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    const current = new WebSocketClass(url.toString()); socket = current;
    const alive = () => valid() && socket === current;
    const armWatchdog = () => {
      clearTimeout(watchdog);
      watchdog = setTimeout(() => { if (alive()) current.close(); }, 45000);
    };
    armWatchdog();
    current.onopen = () => { if (alive()) current.send(JSON.stringify({ token: session })); };
    current.onmessage = ({ data }) => {
      if (!alive()) return;
      let event;
      try { event = JSON.parse(data); } catch { current.close(); return; }
      armWatchdog();
      if (event.type === "ready") delay = 1000;
      if (event.type === "ready" || event.type === "changed") reload();
    };
    current.onerror = () => current.close();
    current.onclose = ({ code }) => {
      if (socket !== current) return;
      socket = null; clearTimeout(watchdog);
      if (!valid() || !active || code === 4401) return;
      timer = setTimeout(connect, delay + Math.random() * 250);
      delay = Math.min(delay * 2, 30000);
    };
  }
  function foreground(value) {
    active = value;
    if (value) { delay = 1000; connect(); } else disconnect();
  }
  const online = () => { disconnect(); connect(); };
  const offline = () => disconnect();
  window.addEventListener("online", online);
  window.addEventListener("offline", offline);
  connect();
  return { foreground, stop() {
    stopped = true; disconnect();
    window.removeEventListener("online", online);
    window.removeEventListener("offline", offline);
  } };
}
